import axios, { AxiosRequestConfig, Method, ResponseType } from "axios";
import { toInteger } from "lodash-es";
import {
  isAccessTokenExpired,
  isTransientAuthError,
  logout,
  refreshTokens,
  authInfo,
} from "@/core/auth";
import { createCustomError, getEnv } from "@/core/util";
import { createResponse, Response } from "@/core/api/response";
import { getParameter } from "@/core/util/env";
import REQUEST_TYPE from "@/core/api/request-types";

export const APIError = createCustomError("APIError");

export interface ApiCallCustomOptions {
  baseURL?: string;
  body?: any;
  headers?: Record<string, string>;
  includeDefaultHeaders?: boolean;
  params?: any;
  preserveResponseBody?: boolean;
  responseType?: string;
  timeoutMs?: number;
}

let BASE_URL;
let TIMEOUT_MS;
let CONNECTOR_TIMEOUT_MS;
const retryCodes = [408, 502, 503, 504, 901];
const retryCount = 1;
const env = getEnv("ENVIRONMENT");
const HOST = env === "PRODUCTION" ? window.location.host : "";
const tenantId = getTenantId();

export function getTenantId(): string {
  if (env === "PRODUCTION") {
    try {
      return window.location.host.split(".")[0];
    } catch (e) {
      console.log("There is a problem on getting tenant id from url.");
      return "cyangate";
    }
  }
  return "cyangate";
}

async function ensureBaseConfig(type: string): Promise<void> {
  if (!BASE_URL && !TIMEOUT_MS) {
    [BASE_URL, TIMEOUT_MS] = [
      await getParameter("REST_URL"),
      toInteger(await getParameter("TIMEOUT_MS")),
    ];
  }
  if (!CONNECTOR_TIMEOUT_MS && type === REQUEST_TYPE.CONNECTOR) {
    CONNECTOR_TIMEOUT_MS = toInteger(
      await getParameter("CONNECTOR_REST_TIMEOUT_MS")
    );
  }
}

function getDefaultHeaders(selectedOrgId?: string): Record<string, string> {
  const headers = authInfo.value
    ? {
        Authorization: authInfo.value?.accessToken,
        "X-TenantId": tenantId,
        "X-OrganizationId": selectedOrgId || authInfo.value?.organizationId,
      }
    : {
        "X-TenantId": tenantId,
      };

  return Object.fromEntries(
    Object.entries(headers).filter(([, value]) => value != null)
  ) as Record<string, string>;
}

function getDefaultBaseUrl(): string {
  return env === "PRODUCTION" ? `https://${HOST}${BASE_URL}` : BASE_URL;
}

function getTimeoutMs(type: string): number {
  return type === REQUEST_TYPE.CONNECTOR ? CONNECTOR_TIMEOUT_MS : TIMEOUT_MS;
}

function shouldRetryAuth(
  endpoint: string,
  response: Readonly<Response>,
  hasRetriedAuth: boolean
): boolean {
  return (
    !hasRetriedAuth &&
    endpoint !== "/auth/refresh" &&
    !isRetryResponse(response) &&
    isAccessTokenExpired(response)
  );
}

async function executeRequest(
  config: AxiosRequestConfig,
  responseOptions: { preserveResponseBody?: boolean } = {}
): Promise<Readonly<Response>> {
  let response: Readonly<Response> | undefined;
  for (let i = 0; i < retryCount; i++) {
    try {
      const axiosResponse = await axios.request(config);
      response = createResponse(axiosResponse, responseOptions);
    } catch (err) {
      if (!axios.isAxiosError(err)) {
        throw err;
      }
      response = createResponse(err);
    }

    if (!isRetryResponse(response)) {
      break;
    }
  }

  if (!response) {
    throw new APIError("Request did not produce a response.");
  }

  return response;
}

export async function apiCall(
  method: Method,
  endpoint: string,
  body?: any,
  type: string = REQUEST_TYPE.STANDART,
  selectedOrgId?: string,
  hasRetriedAuth = false
): Promise<Response> {
  await ensureBaseConfig(type);

  const config: AxiosRequestConfig = {
    method,
    url: endpoint,
    baseURL: getDefaultBaseUrl(),
    timeout: getTimeoutMs(type),
    data: body,
    validateStatus: () => true,
    headers: getDefaultHeaders(selectedOrgId),
  };
  const response = await executeRequest(config);

  if (!shouldRetryAuth(endpoint, response, hasRetriedAuth)) {
    return response;
  }

  try {
    await refreshTokens();
  } catch (e) {
    if (!isTransientAuthError(e)) {
      await logout();
    }
    return response;
  }

  return apiCall(method, endpoint, body, type, selectedOrgId, true);
}

export async function apiCallParams(
  method: Method,
  endpoint: string,
  params?: any,
  type: string = REQUEST_TYPE.STANDART,
  selectedOrgId?: string,
  responseType?: string,
  hasRetriedAuth = false
): Promise<Response> {
  await ensureBaseConfig(type);
  const config: AxiosRequestConfig = {
    method,
    url: endpoint,
    baseURL: getDefaultBaseUrl(),
    timeout: getTimeoutMs(type),
    params,
    validateStatus: () => true,
    headers: getDefaultHeaders(selectedOrgId),
  };
  if (responseType) {
    config.responseType = responseType as ResponseType;
  }

  const response = await executeRequest(config);

  if (!shouldRetryAuth(endpoint, response, hasRetriedAuth)) {
    return response;
  }

  try {
    await refreshTokens();
  } catch (e) {
    if (!isTransientAuthError(e)) {
      await logout();
    }
    return response;
  }

  return apiCallParams(
    method,
    endpoint,
    params,
    type,
    selectedOrgId,
    responseType,
    true
  );
}

export async function apiCallCustom(
  method: Method,
  endpoint: string,
  options: ApiCallCustomOptions = {},
  type: string = REQUEST_TYPE.STANDART,
  selectedOrgId?: string,
  hasRetriedAuth = false
): Promise<Response> {
  await ensureBaseConfig(type);

  const defaultHeaders =
    options.includeDefaultHeaders === false
      ? {}
      : getDefaultHeaders(selectedOrgId);
  const headers = {
    ...defaultHeaders,
    ...options.headers,
  };

  const config: AxiosRequestConfig = {
    method,
    url: endpoint,
    baseURL: options.baseURL ?? getDefaultBaseUrl(),
    timeout: options.timeoutMs ?? getTimeoutMs(type),
    data: options.body,
    params: options.params,
    validateStatus: () => true,
    headers,
  };

  config.headers = Object.fromEntries(
    Object.entries(config.headers).filter(([, value]) => value != null)
  );

  if (options.responseType) {
    config.responseType = options.responseType as ResponseType;
  }

  const response = await executeRequest(config, {
    preserveResponseBody:
      options.preserveResponseBody || endpoint.includes("/wizard/"),
  });

  if (!shouldRetryAuth(endpoint, response, hasRetriedAuth)) {
    return response;
  }

  try {
    await refreshTokens();
  } catch (e) {
    if (!isTransientAuthError(e)) {
      await logout();
    }
    return response;
  }

  return apiCallCustom(method, endpoint, options, type, selectedOrgId, true);
}

function isRetryResponse(response: Response): boolean {
  return retryCodes.includes(response.code);
}

export async function getBaseUrl(): Promise<string> {
  try {
    if (!BASE_URL) {
      BASE_URL = await getParameter("REST_URL");
    }
    return env === "PRODUCTION" ? `https://${HOST}${BASE_URL}` : BASE_URL;
  } catch (e) {
    console.log("There is a problem on getting base url.");
  }
  return "";
}
